// src/pages/ContactPage.js
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const floatAnimation = keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
    100% { transform: translateY(0px); }
`;

const glowAnimation = keyframes`
    0% { box-shadow: 0 0 5px rgba(0, 188, 212, 0.5); }
    50% { box-shadow: 0 0 20px rgba(0, 188, 212, 0.8); }
    100% { box-shadow: 0 0 5px rgba(0, 188, 212, 0.5); }
`;

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const rotateBackground = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const ContactContainer = styled.div`
    position: relative;
    padding: 4rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100vh;
    background: linear-gradient(135deg, #121212 0%, #1a1a1a 100%);
    color: #f1f1f1;
    overflow: hidden;
`;

const BackgroundEffect = styled.div`
    position: fixed;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, transparent 20%, #121212 70%),
                linear-gradient(45deg, rgba(0, 188, 212, 0.1) 25%, transparent 25%),
                linear-gradient(-45deg, rgba(0, 188, 212, 0.1) 25%, transparent 25%);
    background-size: 100% 100%, 20px 20px, 20px 20px;
    animation: ${rotateBackground} 60s linear infinite;
    z-index: 0;
    opacity: 0.5;
`;

const Title = styled.h1`
    font-size: 3rem;
    text-align: center;
    color: #00bcd4;
    margin-bottom: 2rem;
    position: relative;
    z-index: 1;
    animation: ${fadeIn} 1s ease-out;
    text-shadow: 0 0 10px rgba(0, 188, 212, 0.5);

    &:after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 50%;
        transform: translateX(-50%);
        width: 50px;
        height: 3px;
        background: #00bcd4;
        animation: ${glowAnimation} 2s infinite;
    }
`;

const ContactForm = styled.form`
    background: rgba(30, 30, 30, 0.9);
    border-radius: 20px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);
    padding: 2rem;
    width: 100%;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    position: relative;
    z-index: 1;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    animation: ${fadeIn} 1s ease-out 0.3s backwards;

    &:hover {
        animation: ${glowAnimation} 3s infinite;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    animation: ${fadeIn} 1s ease-out ${props => props.delay}s backwards;
`;

const Label = styled.label`
    font-weight: bold;
    color: #f1f1f1;
    font-size: 1.1rem;
`;

const Input = styled.input`
    padding: 0.8rem;
    border: 2px solid #333;
    background: #222;
    color: #f1f1f1;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:focus {
        border-color: #00bcd4;
        box-shadow: 0 0 10px rgba(0, 188, 212, 0.3);
        outline: none;
    }
`;

const TextArea = styled.textarea`
    padding: 0.8rem;
    border: 2px solid #333;
    background: #222;
    color: #f1f1f1;
    border-radius: 8px;
    min-height: 150px;
    resize: vertical;
    transition: all 0.3s ease;

    &:focus {
        border-color: #00bcd4;
        box-shadow: 0 0 10px rgba(0, 188, 212, 0.3);
        outline: none;
    }
`;

const SubmitButton = styled.button`
    padding: 1rem 2rem;
    background: linear-gradient(45deg, #00bcd4, #00e5ff);
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1.1rem;
    font-weight: bold;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 5px 15px rgba(0, 188, 212, 0.4);
    }

    &:active {
        transform: translateY(-1px);
    }

    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
        );
        transition: 0.5s;
    }

    &:hover:before {
        left: 100%;
    }
`;

const FloatingElement = styled.div`
    position: absolute;
    width: ${props => props.size}px;
    height: ${props => props.size}px;
    background: radial-gradient(circle, rgba(0, 188, 212, 0.2) 0%, transparent 70%);
    border-radius: 50%;
    animation: ${floatAnimation} ${props => props.duration}s ease-in-out infinite;
    top: ${props => props.top}%;
    left: ${props => props.left}%;
    z-index: 0;
`;

const ContactPage = () => {
    const [formState, setFormState] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [status, setStatus] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');

        try {
            const response = await fetch('https://formspree.io/f/myzyweqr', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formState)
            });

            if (response.ok) {
                setStatus('success');
                setFormState({ name: '', email: '', message: '' });
                setTimeout(() => setStatus(''), 3000); // Clear status after 3 seconds
            } else {
                setStatus('error');
                setTimeout(() => setStatus(''), 3000);
            }
        } catch (error) {
            setStatus('error');
            setTimeout(() => setStatus(''), 3000);
        }
    };

    return (
        <ContactContainer>
            <BackgroundEffect />
            {[...Array(5)].map((_, i) => (
                <FloatingElement
                    key={i}
                    size={Math.random() * 100 + 50}
                    duration={Math.random() * 4 + 3}
                    top={Math.random() * 80 + 10}
                    left={Math.random() * 80 + 10}
                />
            ))}
            <Title>Contact Me</Title>
            <ContactForm onSubmit={handleSubmit}>
                {status === 'success' && (
                    <div style={{ 
                        padding: '1rem', 
                        backgroundColor: 'rgba(0, 255, 0, 0.1)', 
                        color: '#00ff00',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                    }}>
                        Message sent successfully!
                    </div>
                )}
                {status === 'error' && (
                    <div style={{ 
                        padding: '1rem', 
                        backgroundColor: 'rgba(255, 0, 0, 0.1)', 
                        color: '#ff0000',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                    }}>
                        Failed to send message. Please try again.
                    </div>
                )}
                <FormGroup delay={0.2}>
                    <Label htmlFor="name">Name</Label>
                    <Input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formState.name}
                        onChange={(e) => setFormState({...formState, name: e.target.value})}
                    />
                </FormGroup>
                <FormGroup delay={0.4}>
                    <Label htmlFor="email">Email</Label>
                    <Input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formState.email}
                        onChange={(e) => setFormState({...formState, email: e.target.value})}
                    />
                </FormGroup>
                <FormGroup delay={0.6}>
                    <Label htmlFor="message">Message</Label>
                    <TextArea
                        id="message"
                        name="message"
                        required
                        value={formState.message}
                        onChange={(e) => setFormState({...formState, message: e.target.value})}
                    />
                </FormGroup>
                <SubmitButton 
                    type="submit" 
                    disabled={status === 'sending'}
                    style={{ opacity: status === 'sending' ? 0.7 : 1 }}
                >
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                </SubmitButton>
            </ContactForm>
        </ContactContainer>
    );
};

export default ContactPage;
